<script setup lang="ts">
  import type { Component } from "vue";
  import { computed, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { Layers2, Sigma, MessageCircleQuestion, Blocks } from "lucide-vue-next";
  import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
  import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
  import { useSurveyStore } from "@stores/survey/survey";
  import { CHOICE_COLOR_PALETTES } from "./choiceColorPalettes";
  import { CONTENT_CARD_TYPES, ANALYSIS_TYPES, surveyHasGeoCountry } from "./constants";
  import { useDecodeMessage } from "@/utils/composables/decodeMessage";
  import { useQuestionCardTypes } from "@/utils/composables/questionCard";
  import { useRecodeTypeItems } from "@/composables/useRecodeTypeItems";
  import type { PickerItem } from "@/components/episto-ui/picker";
  import { PickerDialog } from "@/components/episto-ui/picker";
  import ResultsCardPreview from "./ResultsCardPreview.vue";
  import ResultsQuestionPreview from "./ResultsQuestionPreview.vue";

  const { determineIcon } = useQuestionCardTypes(ref({}));

  const props = withDefaults(defineProps<{ insertPosition?: number | null }>(), { insertPosition: null });

  const open = defineModel<boolean>("open", { default: false });

  const { decodeName } = useDecodeMessage();
  const store = useResultsV2Store();
  const cardsStore = useResultsV2CardsStore();
  const localesStore = useSurveyLocalesStore();
  const { analysisLocale } = storeToRefs(localesStore);
  const { availableRecodeItems } = useRecodeTypeItems();
  const { survey } = storeToRefs(useSurveyStore());
  const analysisTypes = computed(() =>
    ANALYSIS_TYPES.filter((item) => item.key !== "geo_map" || surveyHasGeoCountry(survey.value?.targets)),
  );

  interface AvailableCard {
    id: number;
    token?: string;
    name: string;
    type?: string;
    is_for_analytics?: boolean;
    specs?: { question?: Record<string, string>; matrix_type?: string };
  }

  const availableCards = computed<AvailableCard[]>(() => store.availableCards ?? []);
  const viewCards = computed<{ card_id: number | null }[]>(() => cardsStore.cards ?? []);

  const GROUPS = [
    { id: "questions", label: "Questions", description: "Survey answers", icon: MessageCircleQuestion },
    { id: "recodes", label: "Recodes", description: "Derived variables", icon: Layers2 },
    { id: "analysis", label: "Analysis", description: "Statistical views", icon: Sigma },
    { id: "content", label: "Content", description: "Structure and text", icon: Blocks },
  ];

  const SECTIONS = {
    questions: ["Questions"],
    recodes: ["New recode", "Saved recodes"],
    analysis: ["Analysis"],
    content: ["Structure", "Media"],
  };

  const CONTENT_ABOUT: Record<string, string> = {
    section: "Adds a titled divider. Sections fold in view mode and make up the dashboard's table of contents.",
    text: "Adds rich text between charts, as plain text, a callout or a quote.",
    image: "Adds an image, such as a logo, a stimulus or the ad tested, with an optional caption.",
  };

  const ANALYSIS_ABOUT: Record<string, string> = {
    importance_performance:
      "Positions each item of a satisfaction matrix by its score and by its correlation with a global score, to show what to fix first. When a halo flattens the correlations, the card blocks: use Key drivers.",
    key_drivers:
      "Splits what explains a global score across the items of a matrix, as relative weights adding up to 100%, each next to the item's score, to decide where to start. Holds up under the halo that blocks the IPA.",
    turf:
      "Finds the combination of answers of a multiple-choice question that reaches the most distinct respondents (Total Unduplicated Reach), with what each addition brings, to size a portfolio of products, flavours or messages.",
    crosstab: "Crosses questions in rows with questions or populations in columns, with optional significance letters.",
    kpi: "Shows one number, with its base: a share of answers, an average or an NPS.",
    kpi_trend: "Follows one number from one wave to the next, as a line.",
    funnel:
      "Counts the respondents reaching each stage, a question and the answers that count, keeping at each stage only those who passed every stage above it.",
    sankey:
      "Draws how the respondents of each answer to one question split across the answers to another, to read switching or before / after movements.",
    overlap:
      "Shows who picked several answers of a multiple-choice question together: a proportional Venn for two or three answers, a duplication matrix beyond.",
    geo_map:
      "Places the respondents of a zipcode question on the map of their country, by area, tinted on their number or on a metric of another question.",
    word_cloud:
      "Sizes the most used words of an open question on how many respondents wrote them, with palettes, size contrast and layout options.",
  };

  const cardTitle = (card?: AvailableCard) => {
    const q = card?.specs?.question;
    if (!q) return "";
    const locale = analysisLocale.value?.token;
    const value = (locale && q[locale]) || Object.values(q)[0] || "";
    return decodeName(value);
  };

  const timesAdded = (cardId: number) => viewCards.value.filter((c) => c.card_id === cardId).length;

  const items = computed(() => [
    ...(availableCards.value || [])
      .filter((card) => !card.is_for_analytics)
      .map((card) => ({
        key: `question:${card.id}`,
        group: "questions",
        section: "Questions",
        name: card.name,
        description: cardTitle(card),
        searchText: card.token,
        icon: determineIcon(card.type),
        checked: timesAdded(card.id) > 0,
      })),
    ...availableRecodeItems.value.map((item) => ({
      key: `recode:${item.key}`,
      group: "recodes",
      section: "New recode",
      name: item.name,
      description: item.description,
      about: item.about ?? item.description,
      icon: Layers2,
    })),
    ...(availableCards.value || [])
      .filter((card) => card.is_for_analytics)
      .map((card) => ({
        key: `question:${card.id}`,
        group: "recodes",
        section: "Saved recodes",
        name: card.name,
        description: cardTitle(card),
        searchText: card.token,
        icon: Layers2,
        checked: timesAdded(card.id) > 0,
      })),
    ...analysisTypes.value.map((item) => ({
      key: `analysis:${item.key}`,
      group: "analysis",
      section: "Analysis",
      name: item.label,
      description: item.description,
      about: ANALYSIS_ABOUT[item.key] ?? item.description,
      icon: item.icon,
    })),
    ...CONTENT_CARD_TYPES.map((item) => ({
      key: `content:${item.key}`,
      group: "content",
      section: item.key === "image" ? "Media" : "Structure",
      name: item.name,
      description: item.description,
      about: CONTENT_ABOUT[item.key] ?? item.description,
      icon: item.icon,
    })),
  ]);

  const findCard = (id: string) => (availableCards.value || []).find((c) => String(c.id) === id);

  interface PreviewBinding {
    component: Component;
    props: Record<string, unknown>;
  }

  const cardPreview = (previewProps: Record<string, unknown>): PreviewBinding => ({
    component: ResultsCardPreview,
    props: previewProps,
  });

  const previewFor = (item: PickerItem): PreviewBinding => {
    const [family, key] = item.key.split(":");
    if (family === "content" || family === "analysis") return cardPreview({ kind: key });
    if (family === "recode") {
      const example = availableRecodeItems.value.find((recode) => recode.key === key)?.example;
      return cardPreview({ kind: "recode", subtitle: example });
    }
    const card = findCard(key);
    const title = cardTitle(card) || card?.name || "";
    const type = card?.type ?? "";
    if (!card?.is_for_analytics && ["q_open", "q_open_multiple", "ai_chat", "ai_chat_v2"].includes(type))
      return cardPreview({ kind: "verbatims", title });
    if (type === "q_image") return cardPreview({ kind: "media", title });
    if (type === "q_video") return cardPreview({ kind: "video", title });
    if (type === "q_audio") return cardPreview({ kind: "audio", title });
    return { component: ResultsQuestionPreview, props: { cardId: card?.id } };
  };

  const previews = computed<Record<string, PreviewBinding>>(() =>
    Object.fromEntries(items.value.map((item) => [item.key, previewFor(item)])),
  );

  let insertionOffset = 0;
  function nextPosition() {
    if (!props.insertPosition) return null;
    insertionOffset += 1;
    return props.insertPosition + insertionOffset;
  }

  type AddCard = (cardType: string, cardId: number | null, position: number | null) => { id: number } | null;
  const addCardAt = (cardType: string, cardId: number | null) =>
    (cardsStore.addCard as AddCard)(cardType, cardId, nextPosition());

  function addContentBlock(key: string) {
    const newCard = addCardAt("content", null);
    if (newCard) cardsStore.updateCardLocal(newCard.id, { content: { sub_type: key } });
  }

  const KPI_DEFAULT_CONTENT = {
    metric: { type: "options_share", question_id: null, option_codes: [] },
  };

  function addAnalysisCard(key: string) {
    // Every analysis is card_type "analysis"; the variant lives in content.sub_type.
    const newCard = addCardAt("analysis", null);
    if (!newCard) return;
    const content: Record<string, unknown> = { sub_type: key };
    const patch: { content: Record<string, unknown>; options?: Record<string, unknown> } = { content };
    if (key === "crosstab") content.significance = { pairwise: false };
    if (key === "kpi" || key === "kpi_trend") Object.assign(content, KPI_DEFAULT_CONTENT);
    if (key === "kpi") patch.options = { size: "m" };
    cardsStore.updateCardLocal(newCard.id, patch);
  }

  function addQuestion(card: AvailableCard) {
    const newCard = addCardAt(card.is_for_analytics ? "recode" : "question", card.id);
    const isNps = card.type === "q_nps" || (card.type === "q_matrix" && card.specs?.matrix_type === "nps");
    if (newCard && isNps) {
      const npsPalette = CHOICE_COLOR_PALETTES.find((p) => p.id === "nps");
      if (npsPalette) {
        const npsCodes = Array.from({ length: 11 }, (_, i) => String(i));
        cardsStore.updateCardLocal(newCard.id, {
          options: { choice_colors: npsPalette.generate(npsCodes), choice_color_palette: "nps" },
        });
      }
    }
  }

  function addOne(itemKey: string) {
    const [family, key] = itemKey.split(":");
    if (family === "content") return addContentBlock(key);
    if (family === "analysis") return addAnalysisCard(key);
    if (family === "recode") return store.addRecodeCard(nextPosition(), key);
    const card = findCard(key);
    if (card) addQuestion(card);
  }

  function handleSelect(itemKeys: string | string[]) {
    insertionOffset = 0;
    for (const itemKey of Array.isArray(itemKeys) ? itemKeys : [itemKeys]) addOne(itemKey);
  }

  const actionLabelFor = (count: number) =>
    count === 0 ? "Add cards" : count === 1 ? "Add 1 card" : `Add ${count} cards`;
</script>

<template>
  <PickerDialog
    v-model:open="open"
    title="Add cards"
    description="Search questions or add content blocks"
    :groups="GROUPS"
    :items="items"
    :sections="SECTIONS"
    multiple
    action-label="Add cards"
    :action-label-for="actionLabelFor"
    footnote="Click to pick several cards, then add them in one go. They can be reordered and resized afterwards."
    @select="handleSelect">
    <template #preview="{ item }">
      <component
        :is="previews[item.key].component"
        :key="item.key"
        v-bind="previews[item.key].props" />
    </template>
  </PickerDialog>
</template>
